// Admin overview (MUI, /admin-mui). Presentation port of dashboard/admin/OverviewPage —
// same data (getAdminOverview), rebuilt with mui/* primitives + brainTokens.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { getAdminOverview } from "../../lib/brain-api";
import type { AdminOverview } from "../../lib/brain-types";
import { brainTokens } from "../../design/brainTheme";
import { PageHeader, KpiCard, Icon } from "../../mui";

const c = brainTokens.color;

const tileSx = {
  display: "block",
  textDecoration: "none",
  color: "inherit",
  borderRadius: `${brainTokens.radius.panel}px`,
  border: "1px solid transparent",
  transition: "background .12s ease, border-color .12s ease",
  "&:hover": { bgcolor: c.tealTint, borderColor: c.hoverBorder },
};

export function AdminOverviewPageMui() {
  const [o, setO] = useState<AdminOverview | null>(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    getAdminOverview().then(setO).catch(() => setErr(true));
  }, []);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <PageHeader title="Admin overview" eyebrow="Users, integrations, and audit health at a glance" />
      {err && (
        <Box sx={{ fontSize: 13, color: c.dangerText, bgcolor: c.dangerBg, border: `1px solid ${c.dangerBorder}`, borderRadius: `${brainTokens.radius.panel}px`, px: 2, py: 1.25 }}>
          Couldn&apos;t load the admin overview.
        </Box>
      )}
      {!err && !o && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} sx={{ color: c.teal }} />
        </Box>
      )}
      {!err && o && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
          <Box component={Link} to="/admin/users" sx={tileSx}>
            <KpiCard
              label={<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Icon name="users" size={14} /> Users</Box>}
              value={String((o.stats as Record<string, unknown>).total ?? "—")}
            />
          </Box>
          <Box component={Link} to="/admin/integrations" sx={tileSx}>
            <KpiCard
              label={<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Icon name="plug" size={14} /> Integrations</Box>}
              value={`${o.integrations.filter((i) => i.on).length} of ${o.integrations.length} on`}
            />
          </Box>
          <Box component={Link} to="/admin/audit" sx={tileSx}>
            <KpiCard
              label={<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Icon name="clipboard-check" size={14} /> Last audit quality</Box>}
              value={String((o.lastAudit?.avgQuality as number | undefined) ?? "—")}
            />
          </Box>
          <Box component={Link} to="/brain-health" sx={tileSx}>
            <KpiCard
              label={<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Icon name="activity" size={14} /> Health</Box>}
              value={<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Icon name="arrow-right" size={16} /> View</Box>}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}
